import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons/faCircleXmark';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { useTranslation } from 'react-i18next';
import { type CamelCasedStudentNetworkEvent } from 'StudentNetworkEvent';
import { EVENT_TYPE_CONFIGS } from 'StudentNetworkEvent/StudentNetworkEventTypeConfigs';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { Card } from 'Card';
import { LoadMoreButton } from '../components/LoadMoreButton';
import { NetworkErrorCard } from '../components/NetworkErrorCard';
import { EventCard } from '../components/events/EventCard';
import { EventDetail } from '../components/events/EventDetail';
import { useNetworkEvents } from '../hooks/useNetworkEvents';
import { useGetStudentNetworkEventByIdQuery } from '../api/studentNetworkEventsV2Api';
import { useNetworkOutletContext } from '../StudentNetworkV2';
import { useScrollFade } from '../hooks/useScrollFade';
import { ScrollToTopButton } from '../components/ScrollToTopButton';
import { ScrollFadeContainer } from '../components/ScrollFadeContainer';
import { useCrossfade } from '../hooks/useCrossfade';
import '../locales/studentNetworkV2/studentNetworkV2-en.json';

const ONLINE_EVENT_TYPES = EVENT_TYPE_CONFIGS.filter(c => c.online).map(c => c.key);
const IN_PERSON_EVENT_TYPES = EVENT_TYPE_CONFIGS.filter(c => !c.online && c.mappable).map(c => c.key);

// Maps event type keys to locale keys for category headings
function snakeToCamel(str: string) {
    return str.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

type EventTab = 'featured' | 'upcoming' | 'past';

const DEFAULT_TABS: { key: EventTab; labelKey: string }[] = [
    { key: 'featured', labelKey: 'featured' },
    { key: 'upcoming', labelKey: 'upcoming' },
    { key: 'past', labelKey: 'pastEvents' },
];

const SEARCH_TABS: { key: EventTab; labelKey: string }[] = [
    { key: 'upcoming', labelKey: 'upcoming' },
    { key: 'past', labelKey: 'pastEvents' },
];

function InfoTooltip({ text }: { text: string }) {
    const [show, setShow] = useState(false);
    const iconRef = useRef<HTMLSpanElement>(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    const handleEnter = useCallback(() => {
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
        }
        setShow(true);
    }, []);

    return (
        <span ref={iconRef} className="hidden md:inline" onMouseEnter={handleEnter} onMouseLeave={() => setShow(false)}>
            <FontAwesomeIcon icon={faCircleInfo} className="cursor-help text-base text-slate-grey-300" />
            {show &&
                createPortal(
                    <span
                        className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-black/85 px-3 py-1.5 text-sm text-white"
                        style={{ top: pos.top, left: pos.left }}
                    >
                        {text}
                    </span>,
                    document.body,
                )}
        </span>
    );
}

// oxlint-disable-next-line max-lines-per-function, complexity -- view with search/tab/detail/category-filter state and mobile/desktop layouts
export function EventsView() {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.events' });
    const { t: tDetail } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.eventDetail' });
    const [activeTab, setActiveTab] = useState<EventTab>('featured');
    const [selectedEvent, setSelectedEvent] = useState<CamelCasedStudentNetworkEvent | null>(null);
    const [searchActive, setSearchActive] = useState(false);
    const [keywordSearch, setKeywordSearch] = useState('');
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<{ eventTypes: string[]; headingKey: string } | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const mobileScrollRef = useRef<HTMLDivElement>(null);
    const desktopScrollRef = useRef<HTMLDivElement>(null);

    const { fadeTop: showMobileScrollTop } = useScrollFade(mobileScrollRef, 100);
    const { fadeTop: showDesktopScrollTop } = useScrollFade(desktopScrollRef, 100);
    const scrollToTop = useCallback(() => {
        mobileScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        desktopScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const { setMobileNavHidden, setMobileFadeOffset } = useNetworkOutletContext();
    const { fadeStyle, startCrossfade } = useCrossfade();
    const { '*': wildcardPath } = useParams();
    const routeEventId = wildcardPath || undefined;
    const navigate = useNavigate();

    // Deep link: /events/:eventId fetches a single event and auto-selects it
    const { data: deepLinkEvent } = useGetStudentNetworkEventByIdQuery(routeEventId!, { skip: !routeEventId });
    const deepLinkAppliedRef = useRef(false);
    // Coordinates with the route-change effect below: deselectEvent() sets this
    // to true before navigating, so the effect knows the crossfade callback will
    // handle clearing selectedEvent — preventing a flash of the list view.
    const deselectingRef = useRef(false);

    useEffect(() => {
        if (routeEventId && deepLinkEvent && !deepLinkAppliedRef.current) {
            deepLinkAppliedRef.current = true;
            setSelectedEvent(deepLinkEvent);
        }
        if (!routeEventId) {
            deepLinkAppliedRef.current = false;
            // Only clear selectedEvent if deselectEvent isn't handling it via crossfade
            if (!deselectingRef.current) setSelectedEvent(null);
            deselectingRef.current = false;
        }
    }, [routeEventId, deepLinkEvent]);

    useEffect(() => {
        setMobileNavHidden(!!selectedEvent || !!categoryFilter);
        return () => setMobileNavHidden(false);
    }, [selectedEvent, categoryFilter, setMobileNavHidden]);

    useEffect(() => {
        setMobileFadeOffset(0);
        return () => setMobileFadeOffset(0);
    }, [setMobileFadeOffset]);

    useEffect(() => {
        const id = setTimeout(() => setDebouncedKeyword(keywordSearch), 300);
        return () => clearTimeout(id);
    }, [keywordSearch]);

    const filterActive = searchActive || categoryFilter !== null;

    const { events, totalCount, isLoading, isFetching, isError, error, loadMore, hasMore, retry } = useNetworkEvents({
        tab: activeTab,
        keywordSearch: debouncedKeyword,
        eventTypes: categoryFilter?.eventTypes,
    });

    const selectEvent = useCallback(
        (event: CamelCasedStudentNetworkEvent) => {
            deepLinkAppliedRef.current = true;
            navigate(`/student-network/events/${event.id}`, { replace: true });
            startCrossfade(() => setSelectedEvent(event));
        },
        [navigate, startCrossfade],
    );

    const deselectEvent = useCallback(() => {
        deselectingRef.current = true;
        navigate('/student-network/events', { replace: true });
        startCrossfade(() => setSelectedEvent(null));
    }, [navigate, startCrossfade]);

    const openSearch = useCallback(() => {
        setSearchActive(true);
        setCategoryFilter(null);
        setActiveTab(prev => (prev === 'featured' ? 'upcoming' : prev));
        requestAnimationFrame(() => searchInputRef.current?.focus());
    }, []);

    const closeSearch = useCallback(() => {
        setSearchActive(false);
        setKeywordSearch('');
        setCategoryFilter(null);
    }, []);

    const handleCategoryFilter = useCallback(
        (categoryKey: string) => {
            let eventTypes: string[];
            let headingKey: string;

            if (categoryKey === 'online') {
                eventTypes = ONLINE_EVENT_TYPES;
                headingKey = 'onlineEvents';
            } else if (categoryKey === 'in_person') {
                eventTypes = IN_PERSON_EVENT_TYPES;
                headingKey = 'inPersonEvents';
            } else {
                eventTypes = [categoryKey];
                headingKey = `${snakeToCamel(categoryKey)}Events`;
            }

            setCategoryFilter({ eventTypes, headingKey });
            setSearchActive(false);
            setKeywordSearch('');

            // Match the tab to whether the current context is past or upcoming
            if (selectedEvent) {
                const eventEndTime = (selectedEvent as Record<string, unknown>).endTime as number | undefined;
                const eventTime = eventEndTime ?? selectedEvent.startTime ?? 0;
                setActiveTab(eventTime * 1000 < Date.now() ? 'past' : 'upcoming');
            } else {
                setActiveTab(prev => (prev === 'featured' ? 'upcoming' : prev));
            }

            setSelectedEvent(null);
            navigate('/student-network/events', { replace: true });
        },
        // oxlint-disable-next-line react-hooks/exhaustive-deps
        [navigate],
    );

    const visibleTabs = filterActive ? SEARCH_TABS : DEFAULT_TABS;

    const tabs = (
        <div className="flex h-[50px] flex-1 items-center justify-between rounded-[12px] bg-slate-grey-50 px-1.5 md:h-[42px] md:flex-none md:justify-start">
            {visibleTabs.map(tab => (
                <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex-1 whitespace-nowrap rounded-[8px] px-2 py-2 text-base transition-colors md:flex-none md:px-3 md:py-1 ${
                        activeTab === tab.key
                            ? 'border border-slate-grey-200 bg-white text-black shadow-[0_2px_10px_0_rgba(0,0,0,0.1)]'
                            : 'text-slate-grey hover:text-black'
                    }`}
                >
                    {t(tab.labelKey)}
                </button>
            ))}
        </div>
    );

    const headingText = categoryFilter
        ? t(categoryFilter.headingKey)
        : searchActive
          ? t('searchingAllEvents')
          : t('recommendedEvents');

    const emptyMessage = filterActive
        ? t(activeTab === 'past' ? 'noPastEventsMatching' : 'noUpcomingEventsMatching')
        : t('noEvents');
    const heading = (
        <div className="relative mb-4 mt-2 flex items-center justify-center gap-2 md:mt-0 md:justify-start">
            {categoryFilter && (
                <button type="button" onClick={closeSearch} className="absolute start-0 text-lg text-coral md:hidden">
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
            )}
            <span className="text-lg font-semibold uppercase text-black md:text-[20px] md:text-slate-grey">
                {headingText}
            </span>
            {!filterActive && <InfoTooltip text={t('recommendedTooltip')} />}
            {categoryFilter && (
                <button
                    type="button"
                    onClick={() => setCategoryFilter(null)}
                    className="hidden items-center gap-1.5 text-slate-grey-300 md:flex"
                >
                    <FontAwesomeIcon icon={faCircleXmark} className="text-[20px]" />
                    <span className="text-base font-semibold">{t('clearFilter')}</span>
                </button>
            )}
        </div>
    );

    const loadMoreError = isError && events.length > 0;

    const loadMoreButton = (
        <LoadMoreButton
            hasMore={hasMore}
            isFetching={isFetching}
            remaining={totalCount - events.length}
            onClick={loadMore}
            error={loadMoreError}
            onRetry={retry}
        />
    );

    const eventGrid = isLoading ? (
        <div className="flex items-center justify-center p-12">
            <FrontRoyalSpinner className="no-delay no-top-margin" color="force-brand" />
        </div>
    ) : isError && events.length === 0 ? (
        <NetworkErrorCard error={error} onRetry={retry} />
    ) : events.length === 0 ? (
        <div className="py-12 text-center text-base italic text-slate-grey">{emptyMessage}</div>
    ) : (
        <>
            <div className="grid gap-7.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                {events.map(event => (
                    <EventCard
                        key={event.id}
                        event={event}
                        onClick={() => selectEvent(event)}
                        onCategoryFilter={handleCategoryFilter}
                    />
                ))}
            </div>
            {loadMoreButton}
        </>
    );

    // Sketch: search input — #f6f7f9 bg, 8px corners, border #010101 when active (focused)
    const desktopSearchInput = (
        <div
            className={`flex h-12 items-center gap-2 rounded-[8px] bg-slate-grey-50 px-4 ${
                searchActive
                    ? 'w-[284px] min-w-0 border border-black'
                    : 'w-[340px] min-w-0 border border-transparent transition-colors hover:border-slate-grey-300'
            }`}
        >
            <FontAwesomeIcon icon={faMagnifyingGlass} className="shrink-0 text-base text-slate-grey" />
            <input
                ref={searchActive ? searchInputRef : undefined}
                type="text"
                value={keywordSearch}
                onChange={e => setKeywordSearch(e.target.value)}
                onFocus={() => {
                    if (!searchActive) openSearch();
                }}
                placeholder={t('search')}
                className="flex-1 bg-transparent text-base text-black outline-none placeholder:text-slate-grey"
            />
        </div>
    );

    // Sketch: close button — #4d5960 bg, 10px corners, white X icon
    const closeButton = (
        <button
            type="button"
            onClick={closeSearch}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-[#4d5960]"
        >
            <FontAwesomeIcon icon={faXmark} className="text-base text-white" />
        </button>
    );

    // Mobile: search input (active state) — 310x42, #f6f7f9 bg, 8px corners
    const mobileSearchInput = (
        <div className="flex h-[50px] flex-1 items-center gap-2 rounded-[8px] bg-slate-grey-50 px-3">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="text-base text-slate-grey" />
            <input
                ref={searchActive ? searchInputRef : undefined}
                type="text"
                value={keywordSearch}
                onChange={e => setKeywordSearch(e.target.value)}
                placeholder={t('search')}
                className="flex-1 bg-transparent text-base text-black outline-none placeholder:text-slate-grey"
            />
        </div>
    );

    const mobileCloseButton = (
        <button
            type="button"
            onClick={closeSearch}
            className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-[10px] bg-[#4d5960]"
        >
            <FontAwesomeIcon icon={faXmark} className="text-base text-white" />
        </button>
    );

    // Mobile: search toggle — #f6f7f9 bg, 10px corners, search icon
    const mobileSearchToggle = (
        <button
            type="button"
            onClick={openSearch}
            className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-[10px] bg-slate-grey-50"
        >
            <FontAwesomeIcon icon={faMagnifyingGlass} className="text-base text-black" />
        </button>
    );

    return (
        <div ref={containerRef} className="h-full overflow-hidden">
            {/* Mobile */}
            <div className="flex h-full flex-col overflow-hidden bg-white md:hidden" style={fadeStyle}>
                {/* Fixed header */}
                <div className="shrink-0 px-[15px] pb-3 pt-3">
                    {searchActive ? (
                        <>
                            {heading}
                            <div className="mb-3 flex items-center gap-2">
                                {mobileSearchInput}
                                {mobileCloseButton}
                            </div>
                            {tabs}
                        </>
                    ) : categoryFilter ? (
                        <>
                            {heading}
                            <div className="flex items-center gap-2">
                                {tabs}
                                {mobileSearchToggle}
                            </div>
                        </>
                    ) : (
                        <>
                            {heading}
                            <div className="flex items-center gap-2">
                                {tabs}
                                {mobileSearchToggle}
                            </div>
                        </>
                    )}
                </div>
                {/* Scrollable content */}
                <ScrollFadeContainer
                    externalRef={mobileScrollRef}
                    className="min-h-0 flex-1 overflow-y-auto px-[15px] pb-5"
                >
                    <ScrollToTopButton visible={showMobileScrollTop} onClick={scrollToTop} className="top-7" />
                    {eventGrid}
                </ScrollFadeContainer>
            </div>

            {/* Desktop detail — rendered on top when an event is selected */}
            {selectedEvent && (
                <ScrollFadeContainer
                    className="hidden max-h-full w-full max-w-[1029px] overflow-y-auto pb-7.5 scrollbar-hide md:block"
                    style={fadeStyle}
                >
                    <Card padding={0} radius={8} className="w-full">
                        <EventDetail
                            event={selectedEvent}
                            onBack={deselectEvent}
                            onCategoryFilter={handleCategoryFilter}
                        />
                    </Card>
                </ScrollFadeContainer>
            )}

            {/* Desktop list — always mounted (hidden when detail is shown) to
                preserve scroll position across select/deselect transitions */}
            <div
                className={`max-h-full w-full max-w-[1560px] flex-col pb-7.5 ${
                    selectedEvent ? 'hidden' : 'hidden md:flex'
                }`}
                style={selectedEvent ? undefined : fadeStyle}
            >
                <div className="flex min-h-0 flex-col rounded-8 border border-slate-grey-200 bg-white shadow-[0_2px_10px_0_rgba(0,0,0,0.1)]">
                    {/* Fixed header — always visible */}
                    <div className="shrink-0 px-7.5 pb-4 pt-7.5">
                        <div className="flex items-center justify-between gap-3">
                            {tabs}
                            <div className="flex min-w-0 items-center gap-2">
                                {desktopSearchInput}
                                {searchActive && closeButton}
                            </div>
                        </div>
                    </div>
                    {/* Scrollable content */}
                    <ScrollFadeContainer
                        externalRef={desktopScrollRef}
                        className="relative min-h-0 flex-1 overflow-y-auto px-7.5 pb-7.5 pt-5 scrollbar-hide"
                    >
                        <ScrollToTopButton
                            visible={showDesktopScrollTop}
                            onClick={scrollToTop}
                            className="top-6 z-20"
                        />
                        {heading}
                        {eventGrid}
                    </ScrollFadeContainer>
                </div>
            </div>

            {/* Mobile event detail overlays — fixed so list stays mounted and preserves scroll */}
            {selectedEvent && (
                <>
                    {/* Small mobile (< 768px, no app header) */}
                    <div className="fixed inset-0 z-40 flex flex-col bg-white pb-[55px] notch:pb-[calc(55px+env(safe-area-inset-bottom))] min-[768px]:hidden">
                        <div className="relative flex shrink-0 items-center justify-center px-5 pb-3 pt-[30px] notch:pt-[calc(env(safe-area-inset-top)+30px)]">
                            <button
                                type="button"
                                onClick={deselectEvent}
                                className="absolute start-0 ps-5 text-lg text-primary transition-opacity hover:opacity-70"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <span className="text-lg font-semibold uppercase text-black">{tDetail('event')}</span>
                        </div>
                        <ScrollFadeContainer className="min-h-0 flex-1 overflow-y-auto">
                            <EventDetail
                                event={selectedEvent}
                                onBack={deselectEvent}
                                onCategoryFilter={handleCategoryFilter}
                                mobileHeaderHidden
                            />
                        </ScrollFadeContainer>
                    </div>
                    {/* Mid-width (768px–992px): app header visible */}
                    <div className="fixed inset-x-0 bottom-0 top-[110px] z-40 hidden flex-col bg-white min-[768px]:flex md:hidden">
                        <div className="relative flex shrink-0 items-center justify-center px-5 py-3">
                            <button
                                type="button"
                                onClick={deselectEvent}
                                className="absolute start-0 ps-5 text-lg text-primary transition-opacity hover:opacity-70"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <span className="text-lg font-semibold uppercase text-black">{tDetail('event')}</span>
                        </div>
                        <ScrollFadeContainer className="min-h-0 flex-1 overflow-y-auto">
                            <EventDetail
                                event={selectedEvent}
                                onBack={deselectEvent}
                                onCategoryFilter={handleCategoryFilter}
                                mobileHeaderHidden
                            />
                        </ScrollFadeContainer>
                    </div>
                </>
            )}
        </div>
    );
}
